<template>
  <ShopCartToastContainer
    v-if="isPdp"
    class="mb-12"
    :position="'bottom-right'"
    :custom-class="'toast-product-details'"
  />

  <action-bar
    v-if="isPdp && !productStore.decoratedProduct?.isDiscontinued"
    ref="actionBarRef"
    :is-expanded="actionBarExpanded"
    :show-checkmark="true"
    :expanded-headline="
      $textDictionary['actionBar.expandedHeadline'] ??
      'actionBar.expandedHeadline'
    "
    :close-button-label="
      $textDictionary['actionBar.closeButtonLabel'] ??
      'actionBar.closeButtonLabel'
    "
    @close="onCloseActionBar"
  >
    <template #mobile-content>
      <product-detail-action-bar-mobile
        :is-initial-loading="isInitialLoading"
        :add-to-cart-button-state="addToCartButtonState"
        :add-to-cart-button-disabled="addToCartButtonDisabled"
        :price-text="productPriceStore.priceText"
        :logged-in="loggedIn"
        :display-add-to-cart-button="displayAddToCartButton"
        @add-to-cart="handleAddToCart"
        @show-options="handleShowOptions"
      />
    </template>
    <template #initial-content>
      <product-detail-action-bar-initial
        :delivery-options="deliveryOptions"
        :delivery-options-tooltip-text="deliveryOptionsTooltipText"
        :is-initial-loading="isInitialLoading"
        :price-text="productPriceStore.priceText"
        :previous-price-text="productStore.decoratedProduct?.previousPriceText"
        :discount-percentage-text="
          productPriceStore?.discountPercentageText ?? ''
        "
        :show-tariff-information="showTariffInformation"
        :pdp-tariff-text="pdpTariffText"
        :amount="amount"
        :add-to-cart-button-state="addToCartButtonState"
        :add-to-cart-button-disabled="addToCartButtonDisabled"
        :request-quote-button-state="requestQuoteButtonState"
        :request-quote-button-disabled="requestQuoteButtonDisabled"
        :logged-in="loggedIn"
        :display-add-to-cart-button="displayAddToCartButton"
        :vsc-link="vscLink"
        :vsc-link-text="vscLinkText"
        @update:amount="handleAmountUpdate"
        @add-to-cart="handleAddToCart"
      />
    </template>
    <template #expanded-content>
      <product-detail-action-bar-expanded
        :product-sku="productStore.decoratedProduct?.sku ?? ''"
        :price-text="productPriceStore.priceText"
        :cart-items="addedCartItems"
        @go-to-cart="handleGoToCart"
        @go-to-checkout="handleGoToCheckout"
      />
    </template>
  </action-bar>
</template>

<script setup lang="ts">
import { ActionBar } from '@hypercodestudio/basler-components';
import type { BtnStateType } from '@hypercodestudio/basler-components/dist/components/elements/Button.vue';
import ProductDetailActionBarMobile from './ProductDetailActionBar/ProductDetailActionBarMobile.vue';
import ProductDetailActionBarInitial from './ProductDetailActionBar/ProductDetailActionBarInitial.vue';
import ProductDetailActionBarExpanded from './ProductDetailActionBar/ProductDetailActionBarExpanded.vue';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import { useProductPriceStore } from '~/stores/productDetailPageStore/productPriceStore';
import { useCartStore } from '~/stores/cartStore/cartStore';
import { useProductAddonsStore } from '~/stores/productDetailPageStore/productAddonsStore';
import { useCrossSellingProductsStore } from '~/stores/productDetailPageStore/crossSellingProductsStore';
import { useAccessoryAssistantStore } from '~/stores/productDetailPageStore/accessoryAssistantStore';
import { isDefined } from '~/utils/guards/isDefined';
import { isProductDetailPage } from '~/utils/shop/isProductDetailPage';

import { useHTMLDictionary } from '~/composables/useHTMLDictionary';
import isSoftwareProduct from '~/utils/shop/isSoftwareProduct';
import type { DeliveryOption } from '@hypercodestudio/basler-components/dist/components/modules/ProductDetailAddToCart.vue';
import { type CartType, CartTypes } from '~/stores/cartStore/model';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import buildCustomerAreaUrl from '~/utils/shop/buildCustomerAreaUrl';

type CartItemInfo = {
  quantity: number;
  decoratedProduct: DecoratedProduct | any;
  config_id?: string;
};

const logger = useLogger();
const productAddonStore = useProductAddonsStore();
const crossSellingProductsStore = useCrossSellingProductsStore();
const productStore = useProductDetailPageStore();
const accessoryAssistantStore = useAccessoryAssistantStore();
const cartStore = useCartStore();
const productPriceStore = useProductPriceStore();
const { loggedIn } = useOidcAuth();
const pdpConfiguration = usePdpConfiguration();
const router = useRouter();

const isPdp = computed(() => isProductDetailPage());

const actionBarRef = ref<InstanceType<typeof ActionBar> | null>(null);
const isInitialLoading = ref(true);
const addToCartButtonState = ref<BtnStateType>('initial');
const addToCartButtonDisabled = ref(false);
const requestQuoteButtonState = ref<BtnStateType>('initial');
const requestQuoteButtonDisabled = ref(false);

const amount = ref(1);
const actionBarExpanded = ref(false);
const addedCartItems = ref<readonly CartItemInfo[]>([]);

const { $textDictionary, $shopStore, $locale } = useNuxtApp();
const htmlDictionary = useHTMLDictionary([
  'pdp.shippingOption.standard.tooltip.text',
  'pdp.shippingOption.fastDelivery.tooltip.text',
  'pdp.tariff.text'
]);

const pdpTariffText = htmlDictionary.value['pdp.tariff.text'] ?? '';

const selectedAccessories = ref(
  getVSCAccessories(crossSellingProductsStore.selectedCrossSellingSkus)
);

const isSoftware = computed(() =>
  isSoftwareProduct(productStore.decoratedProduct)
);

const isVirtualService = computed(() => {
  return (
    productStore.decoratedProduct?.product?.attribute_set_name ===
    'Pim Virtual Services (virtual_services)'
  );
});

const deliveryOptionsTooltipText = !isSoftware.value
  ? '<div class="rte-text">' +
    htmlDictionary.value['pdp.shippingOption.standard.tooltip.text'] +
    '</div>'
  : undefined;

const deliveryOptions = computed((): DeliveryOption[] => {
  const options: DeliveryOption[] = [];

  if (!productStore.decoratedProduct?.showShippingTime) {
    options.push({
      iconName: isSoftware.value ? 'software' : 'available',
      title: isSoftware.value
        ? $textDictionary.value[
            'pdp.shippingOptionWithoutShippingTime.software.title'
          ] ?? 'pdp.shippingOptionWithoutShippingTime.software.title'
        : $textDictionary.value['pdp.shippingOption.standard.title'] ??
          'pdp.shippingOption.standard.title',
      titleAddition: productStore.decoratedProduct?.shippingTimeText ?? ''
    });
  } else {
    options.push({
      iconName: isSoftware.value ? 'software' : 'available',
      title: isSoftware.value
        ? $textDictionary.value['pdp.shippingOption.software.title'] ??
          'pdp.shippingOption.software.title'
        : $textDictionary.value['pdp.shippingOption.standard.title'] ?? '',
      titleAddition: productStore.decoratedProduct?.shippingTimeText ?? '',
      text: $textDictionary.value['pdp.shippingOption.standard.text'] ?? ''
    });
  }

  if (
    productStore.decoratedProduct?.showShippingTime &&
    htmlDictionary.value['pdp.shippingOption.standard.tooltip.text'] &&
    !isSoftware.value
  ) {
    options[0].tooltip = {
      tag: 'button'
    };
  }

  if (productStore.decoratedProduct?.hasFastDelivery) {
    options.push({
      iconName: 'truck-fast',
      title:
        $textDictionary.value['pdp.shippingOption.fastDelivery.title'] ?? '',
      titleAddition:
        $textDictionary.value['pdp.shippingOption.fastDelivery.time'] ?? '',
      text: $textDictionary.value['pdp.shippingOption.fastDelivery.text'] ?? ''
    });
    if (htmlDictionary.value['pdp.shippingOption.fastDelivery.tooltip.text']) {
      options[1].tooltip = {
        tag: 'button'
      };
    }
  }

  return options;
});

const showTariffInformation = computed(() => {
  return (
    $shopStore.value === 'amer_en' &&
    !isSoftware.value &&
    !isVirtualService.value
  );
});

const displayAddToCartButton = computed(
  () => productStore.decoratedProduct?.isBuyable() ?? false
);

const vscLinkText = computed(() =>
  productStore.decoratedProduct?.showVSC
    ? $textDictionary.value['pdp.cart.vsc.label'] ?? ''
    : ''
);

const vscLink = computed(() => {
  const link = productStore.decoratedProduct?.getVSCLink(
    pdpConfiguration.value,
    productStore.vscPageLink,
    selectedAccessories.value
  );
  return typeof link?.uri === 'string' ? link.uri : undefined;
});

function getVSCAccessories(selectedCrossSellingSkus: readonly string[]) {
  if (selectedCrossSellingSkus.length) {
    return `;accessories=[${selectedCrossSellingSkus.toString()}]`;
  }
  return;
}

onMounted(() => {
  if (productStore.decoratedProduct) {
    isInitialLoading.value = false;
  }
});

function handleAmountUpdate(value: number) {
  amount.value = value;
}

function onCloseActionBar() {
  addToCartButtonState.value = 'initial';
  requestQuoteButtonState.value = 'initial';
  addedCartItems.value = [];
  actionBarExpanded.value = false;
  // The ActionBar component handles the mobile view state internally after collapse
}

/**
 * Helper function to get DecoratedProduct from addon store by option UID
 */
function getAddonDecoratedProduct(optionUid: string): DecoratedProduct | null {
  const option = productAddonStore.optionsMap[optionUid];
  if (!option?.product) {
    return null;
  }

  return DecoratedProduct.build(
    option.product as any,
    $textDictionary.value,
    $locale.value
  );
}

/**
 * Helper function to get DecoratedProduct from cross-selling store by SKU
 */
async function getCrossSellingDecoratedProduct(
  sku: string
): Promise<DecoratedProduct | null> {
  // Try to get from store first
  try {
    const stateObj = crossSellingProductsStore._state;
    if (stateObj && stateObj.crossSellingProductsMap) {
      const productsMap = stateObj.crossSellingProductsMap.value as Record<
        string,
        any
      >;
      if (productsMap && productsMap[sku]) {
        return DecoratedProduct.build(
          productsMap[sku] as any,
          $textDictionary.value,
          $locale.value
        );
      }
    }
  } catch (error) {
    logger.warn('Error accessing cross-selling products from store', error);
  }

  // Fallback: Load product from API
  try {
    const response = await $fetch<any>('/api/magento/products', {
      retry: 0,
      query: {
        store: $shopStore.value,
        locale: $locale.value,
        filters: JSON.stringify({}),
        skus: sku
      }
    });

    if (response?.items && response.items.length > 0) {
      return DecoratedProduct.build(
        response.items[0] as any,
        $textDictionary.value,
        $locale.value
      );
    }
  } catch (error) {
    logger.error(`Failed to load product with SKU ${sku}`, error);
  }

  return null;
}

async function handleAddToCart(type: CartType) {
  if (!productStore.decoratedProduct) {
    logger.warn('no active product given - aborting addToCart');
    return;
  }
  addToCartButtonState.value = 'initial';
  requestQuoteButtonState.value = 'initial';

  addToCartButtonState.value =
    type === CartTypes.CART || type === CartTypes.CUSTOMER_CART
      ? 'loading'
      : 'initial';
  requestQuoteButtonState.value =
    type === CartTypes.REQUEST_CART ? 'loading' : 'initial';
  addToCartButtonDisabled.value = true;
  requestQuoteButtonDisabled.value = true;

  const sku = productStore.decoratedProduct.sku;
  // workaround for proper typing
  const passedQuantity: unknown = amount.value;

  if (
    !sku ||
    // empty user input
    (typeof passedQuantity === 'string' && passedQuantity.trim() === '') ||
    (typeof passedQuantity === 'number' && passedQuantity <= 0)
  ) {
    return;
  }

  // Main product
  const cartItemsForDisplay: CartItemInfo[] = [
    {
      decoratedProduct: productStore.decoratedProduct,
      quantity: amount.value
    }
  ];

  // Prepare cart items with DecoratedProducts for display
  // Add additional addon-products if given
  // @see https://gcp.baslerweb.com/jira/browse/DBP-448
  for (const optionUid of Object.values(productAddonStore.selectedOptions)) {
    const decoratedProduct = getAddonDecoratedProduct(optionUid);
    if (decoratedProduct) {
      cartItemsForDisplay.push({
        decoratedProduct,
        quantity: 1
      });
    }
  }

  // Add additional cross-selling products if given
  // @see https://gcp.baslerweb.com/jira/browse/DBP-1176
  for (const crossSellingSku of crossSellingProductsStore.selectedCrossSellingSkus) {
    const decoratedProduct = await getCrossSellingDecoratedProduct(
      crossSellingSku
    );
    if (decoratedProduct) {
      cartItemsForDisplay.push({
        decoratedProduct,
        quantity: amount.value // same quantity as the product itself! e.g. 2 cameras with 2 cables (via cross selling, for each cam one cable)
      });
    }
  }

  // Add accessory if selected
  if (accessoryAssistantStore?.selectedProduct) {
    cartItemsForDisplay.push({
      decoratedProduct: accessoryAssistantStore.selectedProduct,
      quantity: 1
    });
  }

  // For the actual cart API call, we still use SKUs for addon and cross-selling products
  const cartItems = [
    {
      decoratedProduct: productStore.decoratedProduct,
      quantity: amount.value
    },
    // add additional addon-products if given
    ...productAddonStore.selectedOptionSkus.map((sku) => ({
      quantity: 1,
      sku
    })),
    // add additional cross-selling products if given
    ...crossSellingProductsStore.selectedCrossSellingSkus.map((sku) => ({
      quantity: amount.value,
      sku
    }))
  ];

  const accessory = accessoryAssistantStore?.selectedProduct?.sku
    ? {
        quantity: 1,
        sku: accessoryAssistantStore?.selectedProduct?.sku ?? ''
      }
    : undefined;

  if (accessory) {
    cartItems.push(accessory);
  }

  await cartStore.addProducts(cartItems, type);
  addToCartButtonDisabled.value = false;
  requestQuoteButtonDisabled.value = false;
  addToCartButtonState.value =
    (type === CartTypes.CART || type === CartTypes.CUSTOMER_CART) &&
    cartStore.lastAction?.type === 'addItems' &&
    cartStore.lastAction?.success &&
    !isDefined(cartStore.lastAction?.error)
      ? 'success'
      : (type === CartTypes.CART || type === CartTypes.CUSTOMER_CART) &&
        cartStore.lastAction?.type === 'addItems' &&
        isDefined(cartStore.lastAction?.error)
      ? 'error'
      : 'initial';
  requestQuoteButtonState.value =
    type === CartTypes.REQUEST_CART &&
    cartStore.lastAction?.type === 'addItems' &&
    cartStore.lastAction?.success &&
    !isDefined(cartStore.lastAction?.error)
      ? 'success'
      : type === CartTypes.REQUEST_CART &&
        cartStore.lastAction?.type === 'addItems' &&
        isDefined(cartStore.lastAction?.error)
      ? 'error'
      : 'initial';

  if (
    cartStore.lastAction?.type === 'addItems' &&
    cartStore.lastAction?.success &&
    !isDefined(cartStore.lastAction?.error) &&
    (type === CartTypes.CART || type === CartTypes.CUSTOMER_CART)
  ) {
    actionBarExpanded.value = true;
    addedCartItems.value = cartItemsForDisplay;
  }
}

function handleShowOptions() {
  // Toggle the mobile view to show buying options
  actionBarRef.value?.toggleMobile();
}

function handleGoToCart() {
  if (cartStore.currentCartLink) {
    enhancedNavigateTo({ path: cartStore.currentCartLink });
  }
}

function handleGoToCheckout() {
  const checkoutUrl = buildCustomerAreaUrl('cartCheckoutPage');

  if (checkoutUrl) {
    enhancedNavigateTo({ path: checkoutUrl });
  }
}

watch(
  () => crossSellingProductsStore.selectedCrossSellingSkus,
  () => {
    if (crossSellingProductsStore.selectedCrossSellingSkus) {
      selectedAccessories.value = getVSCAccessories(
        crossSellingProductsStore.selectedCrossSellingSkus
      );
    }
  }
);
</script>

<style>
.toast-product-details {
  bottom: var(--action-bar-height) !important;
}
</style>
